import React from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Icon from '@mdi/react';
import { mdiLoading, mdiPencil, mdiPlus, mdiTrashCan, mdiWifi } from '@mdi/js';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { HotspotDTO } from '@webip/shared/src/dto/hotspot';
import HotspotConfigModal from '../appliances/HotspotConfigModal';

export default function HotspotPage() {
    const qc = useQueryClient();

    const [showModal, setShowModal] = React.useState(false);
    const [modalHotspot, setModalHotspot] = React.useState<HotspotDTO | null>(null);

    const queryHotspots = useQuery(
        ['hotspot-list'],
        async () => {
            const res = await axios.get<{ ok: boolean; result: HotspotDTO[] }>(
                `/api/hotspot`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        { retry: false },
    );

    const mutDelete = useMutation({
        mutationFn: async (hotspotId: number) => {
            await axios.delete(`/api/hotspot/${hotspotId}`, { withCredentials: true });
        },
        onSuccess: () => {
            toastSuccess('Hotspot removido!');
            qc.invalidateQueries(['hotspot-list']);
        },
        onError: () => toastError('Erro ao remover hotspot'),
    });

    const handleEdit = (h: HotspotDTO) => {
        setModalHotspot(h);
        setShowModal(true);
    };

    const handleNew = () => {
        setModalHotspot(null);
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setModalHotspot(null);
        qc.invalidateQueries(['hotspot-list']);
    };

    const cabecalhos: DataTableHeader[] = [
        { label: 'Nome', column: 'nome' },
        { label: 'Descrição', column: 'descricao' },
        {
            label: 'Status', render: (h) => (
                <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: h.ativo ? 'var(--status-online-bg)' : 'var(--status-offline-bg)', color: h.ativo ? 'var(--status-online)' : 'var(--status-offline)' }}>
                    {h.ativo ? 'Ativo' : 'Inativo'}
                </span>
            )
        },
        {
            label: '', render: (h) => (
                <HBox style={{ gap: '6px', justifyContent: 'flex-end' }}>
                    <Botao variant="outline-primary" size="sm" icon={mdiPencil} onClick={() => handleEdit(h)}>Editar</Botao>
                    <Botao variant="outline-danger" size="sm" icon={mdiTrashCan} onClick={() => { if (confirm(`Remover hotspot "${h.nome}"?`)) mutDelete.mutate(h.id!); }} disabled={mutDelete.isLoading} />
                </HBox>
            )
        },
    ];

    const linhas = queryHotspots.data ?? [];

    return (
        <LayoutUsuario>
            <VBox style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title="Hotspot (Portal Cativo)"
                    description="Gerencie os portais cativos. Cada hotspot pode ser vinculado a uma ou mais LANs."
                    actions={(
                        <Botao
                            variant="primary"
                            icon={mdiPlus}
                            onClick={handleNew}
                        >
                            Novo Hotspot
                        </Botao>
                    )}
                />

                {queryHotspots.isLoading && (
                    <HBox style={{ gap: '8px', color: 'var(--tc-color-gray-500)' }}>
                        <Icon path={mdiLoading} size="20px" spin />
                        <span>Carregando...</span>
                    </HBox>
                )}

                {!queryHotspots.isLoading && linhas.length === 0 && (
                    <VBox style={{ alignItems: 'center', gap: '12px', padding: '32px' }}>
                        <Icon path={mdiWifi} size="48px" style={{ color: 'var(--tc-color-gray-300)' }} />
                        <span style={{ color: 'var(--tc-color-gray-500)' }}>
                            Nenhum hotspot cadastrado ainda.
                        </span>
                        <Botao variant="primary" icon={mdiPlus} onClick={handleNew}>
                            Novo Hotspot
                        </Botao>
                    </VBox>
                )}

                {!queryHotspots.isLoading && linhas.length > 0 && (
                    <DataTable cabecalhos={cabecalhos} linhas={linhas} />
                )}
            </VBox>

            <HotspotConfigModal
                hotspot={modalHotspot}
                show={showModal}
                onClose={handleClose}
            />
        </LayoutUsuario>
    );
}
